import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import EditorSectionSelector from '../EditorSectionSelector';
import { databaseService, storageService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

// Import the three sections from RichTextEditorPage
import BasicProjectInformation from './sections/BasicProjectInformation';
import FullDescriptionEditor from './sections/FullDescriptionEditor';
import VintageLinkManagement from '../VintageLinkManagement';

const BLOG_COLLECTION = 'blogs';
const BLOG_BUCKET = 'blog_images';
const BLOG_CATEGORIES_COLLECTION = 'blog_categories';

export default function BlogRichTextEditor({ 
  blogData = null, 
  onSave, 
  onCancel,
  isEditing = false 
}) {
  const [selectedSection, setSelectedSection] = useState(null);
  const [isDraft, setIsDraft] = useState(true);
  const [currentBlogId, setCurrentBlogId] = useState(isEditing && blogData?.$id ? blogData.$id : null);
  const { showToast } = useToast();
  
  // Form data state - mapped to blog structure
  const [formData, setFormData] = useState({
    // Basic Project Information (mapped to blog fields)
    title: '',
    category: '',
    subcategoryId: '', // Not used in blogs, but kept for compatibility
    description: '',
    fullDescription: '',
    thumbnail: null,
    thumbnailUrl: '',
    gallery: [],
    galleryUrls: [],
    likes: 0,
    featured: false,
    projectDetails: [
      { label: 'Author', value: '' },
      { label: 'Publish Date', value: '' },
      { label: 'Category', value: '' },
      { label: 'Tags', value: '' }
    ],
    projectLink: '', // Not used in blogs
    tags: [],
    
    // Link Management (mapped to blog fields)
    customSlug: '',
    useProjectPrefix: true,
    
    // Author Information (blog specific)
    authorNames: [''],
    authorImages: [''],
    publishDate: new Date().toISOString().split('T')[0],
  });

  // Initialize form data when editing
  useEffect(() => {
    if (isEditing && blogData) {
      setFormData({
        title: blogData.title || '',
        category: blogData.category || '',
        subcategoryId: '',
        description: blogData.description || '',
        fullDescription: blogData.fullDescription || '',
        thumbnail: null,
        thumbnailUrl: blogData.thumbnailUrl || '',
        gallery: [],
        galleryUrls: blogData.galleryUrls || [],
        likes: blogData.likes || 0,
        featured: blogData.featured || false,
        projectDetails: [
          { label: 'Author', value: blogData.authorNames?.[0] || '' },
          { label: 'Publish Date', value: blogData.publishDate || '' },
          { label: 'Category', value: blogData.category || '' },
          { label: 'Tags', value: blogData.tags?.join(', ') || '' }
        ],
        projectLink: '',
        tags: blogData.tags || [],
        customSlug: blogData.customSlug || '',
        useProjectPrefix: blogData.useProjectPrefix !== undefined ? blogData.useProjectPrefix : true,
        authorNames: blogData.authorNames || [''],
        authorImages: blogData.authorImages || [''],
        publishDate: blogData.publishDate || new Date().toISOString().split('T')[0],
      });
      setIsDraft(!blogData.published); // Assume published field exists or derive from data
    }
  }, [isEditing, blogData]);

  // Handle section save
  const handleSectionSave = async (sectionData) => {
    try {
      // Update form data with section data
      setFormData(prev => ({ ...prev, ...sectionData }));
      
      // Save as draft to database
      await saveDraft({ ...formData, ...sectionData });
      
      showToast('Section saved as draft successfully!', 'success');
      setSelectedSection(null); // Go back to section selector
    } catch (error) {
      console.error('Error saving section:', error);
      showToast('Failed to save section. Please try again.', 'error');
    }
  };

  // Save as draft function
  const saveDraft = async (data = formData) => {
    try {
      // Upload thumbnail if needed
      let thumbnailUrl = data.thumbnailUrl;
      if (data.thumbnail) {
        thumbnailUrl = await uploadImage(data.thumbnail);
        if (!thumbnailUrl) {
          throw new Error('Failed to upload thumbnail');
        }
      }

      // Upload gallery images if needed
      const finalGalleryUrls = [];
      if (data.gallery.length > 0) {
        for (let i = 0; i < data.gallery.length; i++) {
          const file = data.gallery[i];
          if (file instanceof File) {
            const url = await uploadImage(file);
            if (url) finalGalleryUrls.push(url);
          } else {
            if (data.galleryUrls[i] && !data.galleryUrls[i].startsWith('blob:')) {
              finalGalleryUrls.push(data.galleryUrls[i]);
            }
          }
        }
      } else {
        finalGalleryUrls.push(...(data.galleryUrls || []));
      }

      // Filter out empty author names and images
      const filteredAuthorNames = data.authorNames.filter(name => name.trim() !== '');
      const filteredAuthorImages = data.authorImages.filter(img => img.trim() !== '');

      const blogDataToSave = {
        title: data.title,
        category: data.category,
        description: data.description,
        thumbnailUrl: thumbnailUrl,
        galleryUrls: finalGalleryUrls,
        likes: parseInt(data.likes) || 0,
        featured: data.featured,
        fullDescription: data.fullDescription,
        customSlug: data.customSlug,
        useProjectPrefix: data.useProjectPrefix,
        authorNames: filteredAuthorNames,
        authorImages: filteredAuthorImages,
        publishDate: data.publishDate,
        tags: data.tags || [],
        published: false, // Always false for draft
        isDraft: true,
      };

      let result;
      if (currentBlogId) {
        // Update existing blog
        result = await databaseService.updateDocument(
          BLOG_COLLECTION,
          currentBlogId,
          blogDataToSave
        );
      } else {
        // Create new blog
        result = await databaseService.createDocument(
          BLOG_COLLECTION,
          blogDataToSave
        );
        
        // Update currentBlogId after successful creation
        if (result.success) {
          setCurrentBlogId(result.data.$id);
        }
      }

      if (!result.success) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  };

  // Publish function
  const handlePublish = async () => {
    try {
      // Validate required fields
      if (!formData.title || !formData.category || !formData.description || !formData.fullDescription) {
        showToast('Please fill in all required fields before publishing.', 'error');
        return;
      }

      // Save with published status
      const publishData = { ...formData, published: true, isDraft: false };
      await saveDraft(publishData);
      
      showToast('Blog published successfully!', 'success');
      setIsDraft(false);
      
      // Call parent onSave callback
      if (onSave) {
        onSave(publishData);
      }
    } catch (error) {
      console.error('Error publishing blog:', error);
      showToast('Failed to publish blog. Please try again.', 'error');
    }
  };

  // Save as draft function (button handler)
  const handleSaveAsDraft = async () => {
    try {
      await saveDraft();
      showToast('Blog saved as draft successfully!', 'success');
      setIsDraft(true);
      
      // Call parent onSave callback
      if (onSave) {
        onSave({ ...formData, published: false, isDraft: true });
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      showToast('Failed to save draft. Please try again.', 'error');
    }
  };

  // Upload image helper
  const uploadImage = async (file) => {
    try {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileId = `${timestamp}_${randomStr}.${fileExt}`;
      
      const result = await storageService.uploadFile(BLOG_BUCKET, fileId, file);
      if (result.success) {
        const fileUrl = storageService.getFileView(BLOG_BUCKET, result.data.$id);
        return fileUrl;
      }
      throw new Error(result.error || 'Failed to upload image');
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // Handle section selection
  const handleSectionSelect = (sectionId) => {
    setSelectedSection(sectionId);
  };

  // Handle back to selector
  const handleBackToSelector = () => {
    setSelectedSection(null);
  };

  // Render section content
  const renderSectionContent = () => {
    switch (selectedSection) {
      case 'basic-info':
        return (
          <BasicProjectInformation
            formData={formData}
            onSave={handleSectionSave}
            onBack={handleBackToSelector}
            isBlogMode={true}
          />
        );
      case 'full-description':
        return (
          <FullDescriptionEditor
            formData={formData}
            onSave={handleSectionSave}
            onBack={handleBackToSelector}
            isBlogMode={true}
          />
        );
      case 'link-management':
        return (
          <VintageLinkManagement
            formData={formData}
            onSave={handleSectionSave}
            onBack={handleBackToSelector}
            isBlogMode={true}
          />
        );
      default:
        return null;
    }
  };

  if (selectedSection) {
    return (
      <div className="min-h-screen bg-[#FFFAEB]">
        {renderSectionContent()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFAEB]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Blog Management
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-xl font-semibold" style={{ color: '#105652' }}>
              {isEditing ? 'Edit Blog' : 'Create New Blog'} - Advanced Editor
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isDraft 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-green-100 text-green-800'
            }`}>
              {isDraft ? 'Draft' : 'Published'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <EditorSectionSelector 
          onSelectSection={handleSectionSelect}
          onSaveAsDraft={handleSaveAsDraft}
          onPublish={handlePublish}
        />
      </div>
    </div>
  );
}